import type { Ref, ToRefs } from "vue";
import type { EditorProps } from "../editor.types";
import { createEventDispatcher } from "@/runtime/shared/events";
import { loadMonaco, loadMonacoCss } from "../monaco-loader";
import { resolveTheme } from "./resolveTheme";
import { buildEditorOptions } from "./buildEditorOptions";
import { registerGraphQL, registerHTTP, registerPHP, registerBash } from "./languages";

export const EDITOR_PAD = 24;
export const EDITOR_MAX_LINES = 32;

const getMonaco = (): typeof monaco | undefined =>
  (globalThis as typeof globalThis & { monaco?: typeof monaco }).monaco;

export interface EditorMutableState {
  editor: monaco.editor.IStandaloneCodeEditor | null;
  contentDisposer: monaco.IDisposable | null;
  focusDisposer: monaco.IDisposable | null;
  blurDisposer: monaco.IDisposable | null;
  resizeObserver: ResizeObserver | null;
  autoHeader: HTMLElement | null;
  disposed: boolean;
  settingValue: boolean;
  cachedLineHeight: number | null;
  resizeRafId: number | null;
  heightRafId: number | null;
  ariaContainer: Element | null;
  /** True when updateEditorHeight just set the container height and already
   *  called layout(). The ResizeObserver callback skips its layout() call
   *  to avoid a redundant second layout in the next rAF. */
  justSetHeight: boolean;
  /** Last scrollbar vertical state applied, to skip redundant updateOptions. */
  lastScrollbarVertical: "auto" | "hidden" | null;
}

export const createMutableState = (): EditorMutableState => {
  return {
    editor: null,
    contentDisposer: null,
    focusDisposer: null,
    blurDisposer: null,
    resizeObserver: null,
    autoHeader: null,
    disposed: false,
    settingValue: false,
    cachedLineHeight: null,
    resizeRafId: null,
    heightRafId: null,
    ariaContainer: null,
    justSetHeight: false,
    lastScrollbarVertical: null,
  };
};

const getEditorLineCount = (editor: monaco.editor.IStandaloneCodeEditor): number => {
  return editor.getModel()?.getLineCount() ?? 1;
};

/**
 * Measures and caches the line height by temporarily zeroing editor padding so
 * getContentHeight() returns a raw value. The +1 accounts for Monaco's internal
 * phantom line. Padding is intentionally left at 0 — the caller restores it in
 * a single merged updateOptions call to avoid a redundant layout pass.
 */
const calibrateLineHeight = (ms: EditorMutableState): boolean => {
  if (ms.cachedLineHeight !== null || !ms.editor) return false;
  ms.editor.updateOptions({ padding: { top: 0, bottom: 0 } });
  const rawHeight = ms.editor.getContentHeight();
  const lineCount = getEditorLineCount(ms.editor);
  ms.cachedLineHeight = rawHeight / (lineCount + 1);
  return true;
};

export const updateEditorHeight = (ms: EditorMutableState, containerRef: Ref<HTMLElement | null>): void => {
  if (!ms.editor || !containerRef.value) return;
  const didCalibrate = calibrateLineHeight(ms);
  const lineCount = getEditorLineCount(ms.editor);
  const capped = lineCount > EDITOR_MAX_LINES;
  const displayLines = capped ? EDITOR_MAX_LINES : lineCount;
  const lineContentHeight = displayLines * ms.cachedLineHeight!;
  containerRef.value.style.height = `${lineContentHeight + EDITOR_PAD * 2}px`;

  // Build merged options: only restore padding when calibration just zeroed it,
  // and only update scrollbar when capped state has actually changed.
  const newVertical = capped ? "auto" : "hidden";
  const scrollbarChanged = ms.lastScrollbarVertical !== newVertical;
  if (didCalibrate || scrollbarChanged) {
    ms.editor.updateOptions({
      ...(didCalibrate ? { padding: { top: EDITOR_PAD, bottom: EDITOR_PAD } } : {}),
      ...(scrollbarChanged ? { scrollbar: { vertical: newVertical } } : {}),
    });
  }

  if (scrollbarChanged) ms.lastScrollbarVertical = newVertical;

  // Signal the ResizeObserver to skip its rAF layout — we already called layout() below.
  ms.justSetHeight = true;
  ms.editor.layout();
};

export const debouncedHeightUpdate = (ms: EditorMutableState, containerRef: Ref<HTMLElement | null>): void => {
  if (ms.heightRafId !== null) return;
  ms.heightRafId = requestAnimationFrame(() => {
    ms.heightRafId = null;
    updateEditorHeight(ms, containerRef);
  });
};

const findNewAriaContainer = (ariasBefore: Set<Element>): Element | null => {
  for (const el of document.body.querySelectorAll(".monaco-aria-container")) {
    if (!ariasBefore.has(el)) return el;
  }
  return null;
};

const attachEditorListeners = (
  ms: EditorMutableState,
  containerRef: Ref<HTMLElement | null>,
  props: Readonly<EditorProps>,
  dispatchEvent: ReturnType<typeof createEventDispatcher>,
): void => {
  ms.focusDisposer = ms.editor!.onDidFocusEditorText(() => {
    ms.editor?.updateOptions({ scrollbar: { handleMouseWheel: true } });
  });
  ms.blurDisposer = ms.editor!.onDidBlurEditorText(() => {
    ms.editor?.updateOptions({ scrollbar: { handleMouseWheel: false } });
  });

  if (!props.automaticLayout && containerRef.value) {
    ms.resizeObserver = new ResizeObserver(() => {
      // updateEditorHeight just set height and already called layout() — skip this cycle.
      if (ms.justSetHeight) {
        ms.justSetHeight = false;
        return;
      }
      if (ms.resizeRafId !== null) return;
      ms.resizeRafId = requestAnimationFrame(() => {
        ms.resizeRafId = null;
        ms.editor?.layout();
      });
    });
    ms.resizeObserver.observe(containerRef.value);
  }

  ms.contentDisposer = ms.editor!.onDidChangeModelContent(() => {
    if (ms.settingValue || !ms.editor) return;
    debouncedHeightUpdate(ms, containerRef);
    dispatchEvent("update:value", ms.editor.getValue());
  });
};

const loadAndValidateMonaco = async (
  ms: EditorMutableState,
  shadow: ShadowRoot,
  containerRef: Ref<HTMLElement | null>,
): Promise<typeof monaco | null> => {
  if (!containerRef.value) return null;
  if (!containerRef.value.isConnected || !containerRef.value.parentNode) {
    console.error("MonacoCE: Editor container is not attached to the DOM, skipping init.");
    return null;
  }

  await Promise.all([loadMonacoCss(shadow), loadMonaco()]);
  if (ms.disposed || !containerRef.value?.isConnected) return null;

  const monacoGlobal = getMonaco();
  if (!monacoGlobal?.editor) {
    console.error("MonacoCE: Monaco global not available after load.");
    return null;
  }
  return monacoGlobal;
};

const createEditorInstance = (
  ms: EditorMutableState,
  monacoGlobal: typeof monaco,
  hostContainer: HTMLElement,
  props: Readonly<EditorProps>,
  state: ToRefs<EditorProps>,
): void => {
  const baseTheme = state.monacoTheme?.value ?? props.monacoTheme ?? "vs-dark";
  const theme = resolveTheme(monacoGlobal, baseTheme, props.autoDetectHighContrast !== false);
  const value = state.value?.value ?? props.value ?? "";
  const language = state.language?.value ?? props.language ?? "javascript";
  const baseOptions = buildEditorOptions(props, value, language, theme);
  const ariasBefore = new Set(document.body.querySelectorAll(".monaco-aria-container"));

  ms.editor = monacoGlobal.editor.create(hostContainer, {
    ...baseOptions,
    ...(props.monacoSettings as monaco.editor.IStandaloneEditorConstructionOptions),
  });

  ms.ariaContainer = findNewAriaContainer(ariasBefore);
};

const LANGUAGE_REGISTRARS: Record<string, (monacoGlobal: typeof monaco) => void> = {
  graphql: registerGraphQL,
  http: registerHTTP,
  php: registerPHP,
  bash: registerBash,
};

export const initMonaco = async (
  ms: EditorMutableState,
  shadow: ShadowRoot,
  containerRef: Ref<HTMLElement | null>,
  props: Readonly<EditorProps>,
  state: ToRefs<EditorProps>,
  dispatchEvent: ReturnType<typeof createEventDispatcher>,
): Promise<void> => {
  const monacoGlobal = await loadAndValidateMonaco(ms, shadow, containerRef);
  if (!monacoGlobal || !containerRef.value) return;

  const lang = state.language?.value ?? props.language;
  LANGUAGE_REGISTRARS[lang ?? ""]?.(monacoGlobal);
  createEditorInstance(ms, monacoGlobal, containerRef.value, props, state);
  attachEditorListeners(ms, containerRef, props, dispatchEvent);
  updateEditorHeight(ms, containerRef);
};

const disposeDisposables = (ms: EditorMutableState): void => {
  ms.resizeObserver?.disconnect();
  ms.contentDisposer?.dispose();
  ms.focusDisposer?.dispose();
  ms.blurDisposer?.dispose();
  ms.editor?.dispose();
  ms.ariaContainer?.remove();
};

const cancelPendingFrames = (ms: EditorMutableState): void => {
  if (ms.resizeRafId !== null) cancelAnimationFrame(ms.resizeRafId);
  if (ms.heightRafId !== null) cancelAnimationFrame(ms.heightRafId);
};

export const disposeEditorResources = (
  ms: EditorMutableState,
  host: Element | null,
  handleHeaderCopy: () => void,
): void => {
  ms.disposed = true;
  cancelPendingFrames(ms);
  disposeDisposables(ms);
  if (host) host.removeEventListener("editor-copy", handleHeaderCopy);
  if (ms.autoHeader?.parentNode) ms.autoHeader.remove();
};

export const setupEditorWatchers = (
  ms: EditorMutableState,
  state: ToRefs<EditorProps>,
  containerRef: Ref<HTMLElement | null>,
  resolvedLanguage: { value: string },
  watchFn: typeof import("vue").watch,
): void => {
  watchFn(
    () => resolvedLanguage.value,
    (lang: string) => {
      if (ms.autoHeader) ms.autoHeader.setAttribute("language", lang);
    },
  );

  watchFn(
    () => state.value?.value,
    (v) => {
      if (!ms.editor || v == null || ms.editor.getValue() === v) return;
      ms.settingValue = true;
      const model = ms.editor.getModel();
      if (model) {
        model.applyEdits([{ range: model.getFullModelRange(), text: v, forceMoveMarkers: true }]);
      } else {
        ms.editor.setValue(v);
      }
      ms.settingValue = false;
      debouncedHeightUpdate(ms, containerRef);
    },
  );

  watchFn(
    () => state.language?.value,
    (lang) => {
      if (!ms.editor || !lang) return;
      const monacoGlobal = getMonaco();
      const model = ms.editor.getModel();
      if (monacoGlobal?.editor?.setModelLanguage && model) {
        monacoGlobal.editor.setModelLanguage(model, lang);
      } else {
        ms.editor.updateOptions({ language: lang });
      }
    },
  );

  watchFn(
    () => state.monacoTheme?.value,
    (theme) => {
      if (!theme) return;
      const monacoGlobal = getMonaco();
      if (monacoGlobal?.editor?.setTheme) monacoGlobal.editor.setTheme(theme);
    },
  );
};
